import { trackAdapter } from './trackAdapter';
import { StorageApi, Configuration as SdkConfiguration } from '../../sdk';
import { IGeoProjection } from './GeoProjection';
import { POIData } from '../../engine/poi/POIData';
import { POI } from '../../engine/poi/POI';
import { SceneSettingsData } from '../../engine/core/SceneSettingsData';
import { AppSettingsData } from '../../engine/core/AppSettingsData';
import { ArtrackTrackMeta, ArtrackWaypointMeta } from '../../engine/integrations/artrack/ArtrackMeta';
import { Vector3 } from '../../engine/types';

export interface TrackFactoryOptions {
  projection?: IGeoProjection; // if omitted, no pre-positioning
}

export class TracksFactory {
  constructor(private readonly scene: SceneSettingsData, private readonly app: AppSettingsData) {}

  async createFromTrack(trackId: number, options: TrackFactoryOptions = {}) {
    const track = await trackAdapter.getTrack(trackId);
    const waypoints = await trackAdapter.listWaypointsDetail(trackId);

    const pois: POI[] = [];
    let index = 0;
    // Storage API
    const SDK_BASE_URL = (import.meta as any).env?.VITE_SDK_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const storageApi = new StorageApi(new SdkConfiguration({
      basePath: SDK_BASE_URL,
      baseOptions: { headers: {} }
    }));

    for (const wp of waypoints) {
      const poiData = new POIData(this.scene, this.app);
      poiData.artrackMeta = new ArtrackWaypointMeta(index, {
        lat: wp.latitude,
        lon: wp.longitude,
        alt: wp.altitude ?? undefined,
        time: wp.recordedAt,
        media: (wp.media || []).map(m => ({ url: m.url, thumbnailUrl: m.thumbnailUrl, type: m.type }))
      });

      if (options.projection) {
        const p = await options.projection.project(wp.latitude, wp.longitude, wp.altitude ?? undefined);
        poiData.position.value = new Vector3(p.x, p.y, p.z);
      }

      // Set primary media URI if available (image/video/audio)
      const primary = (wp.media || []).find(m => !!(m?.url || m?.storageObjectId));
      if (primary) {
        if (primary.url) poiData.uri.value = [primary.url];
        // Enrich label from storage object if available
        if (primary.storageObjectId) {
          try {
            const soRes = await storageApi.getObjectMetadataStorageObjectsObjectIdGet(primary.storageObjectId as any, undefined);
            const so: any = soRes.data;
            const title = (so?.title || '').trim();
            const description = (so?.description || '').trim();
            if (title || description) {
              poiData.markdown.value = `# ${title || 'Untitled'}\n\n${description || ''}`;
              poiData.showLabel.value = true;
            }
          } catch (e) {
            // ignore enrichment failures
          }
        }
      }

      // Waypoint id on POI
      (poiData as any).artrack_waypointId.value = wp.id;

      const poi = new POI(`${track.name || 'Track'} #${index + 1}`, poiData, this.scene, this.app);
      pois.push(poi);
      index++;
    }

    const ctx = options.projection?.getContext() || {};
    const trackMeta = new ArtrackTrackMeta(track.id, {
      utmZone: ctx.utmZone,
      hemisphere: ctx.hemisphere,
      originEasting: ctx.originEasting,
      originNorthing: ctx.originNorthing
    });

    return { track, pois, trackMeta };
  }
}
